import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, MapPin, Plane, Filter, Clock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VisaFinderPage = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [originCountry, setOriginCountry] = useState("");
  const [destinationCountry, setDestinationCountry] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterVOA, setFilterVOA] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      setCountries(response.data.countries);
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Failed to load countries");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!destinationCountry) {
      toast.error("Please select a destination country");
      return;
    }
    // Navigate to visa details page with optional origin parameter
    const url = `/visa/${destinationCountry}${originCountry ? `?origin=${originCountry}` : ''}`;
    navigate(url);
  };

  // Also navigate when destination is selected
  const handleDestinationChange = (value) => {
    setDestinationCountry(value);
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         country.code.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesVOA = !filterVOA || country.is_visa_on_arrival;
    return matchesSearch && matchesVOA;
  });

  // Sample origin countries
  const originCountries = [
    { code: "IN", name: "India", flag: "🇮🇳" },
    { code: "US", name: "United States", flag: "🇺🇸" },
    { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
    { code: "CN", name: "China", flag: "🇨🇳" },
    { code: "PH", name: "Philippines", flag: "🇵🇭" },
    { code: "PK", name: "Pakistan", flag: "🇵🇰" },
    { code: "BD", name: "Bangladesh", flag: "🇧🇩" },
    { code: "NG", name: "Nigeria", flag: "🇳🇬" },
    { code: "BR", name: "Brazil", flag: "🇧🇷" },
    { code: "MX", name: "Mexico", flag: "🇲🇽" },
  ];

  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-20" data-testid="visa-finder-page">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="flex items-center gap-2 text-[#E15636] mb-4">
          <Plane className="w-5 h-5" />
          <span className="text-sm font-medium uppercase tracking-widest">Visa Finder</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-heading tracking-tight text-[#0F172A] mb-4">
          Find Your Visa <span className="text-[#E15636]">Requirements</span>
        </h1>
        <p className="text-lg text-[#64748B] max-w-2xl">
          Select your nationality and destination to get detailed visa requirements, 
          processing times, and application forms.
        </p>
      </section>

      {/* Search Panel */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-16">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200" data-testid="search-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Origin Country */}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Your Nationality (Optional)
              </label>
              <Select value={originCountry} onValueChange={setOriginCountry}>
                <SelectTrigger 
                  className="h-12 rounded-lg border-2 border-slate-200 focus:border-[#0F172A]"
                  data-testid="origin-select"
                >
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {originCountries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      <span className="flex items-center gap-2">
                        <span>{country.flag}</span>
                        <span>{country.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Destination Country */}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">
                <Plane className="w-4 h-4 inline mr-1" />
                Destination Country
              </label>
              <Select value={destinationCountry} onValueChange={handleDestinationChange}>
                <SelectTrigger 
                  className="h-12 rounded-lg border-2 border-slate-200 focus:border-[#0F172A]"
                  data-testid="destination-select"
                >
                  <SelectValue placeholder="Where are you going?" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      <span className="flex items-center gap-2">
                        <span>{country.flag}</span>
                        <span>{country.name}</span>
                        {country.is_visa_on_arrival && (
                          <Badge variant="secondary" className="ml-2 text-xs">VOA</Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <Button 
                className="w-full h-12 rounded-full bg-[#0F172A] text-white hover:bg-[#1E293B] shadow-lg"
                onClick={handleSearch}
                disabled={!destinationCountry}
                data-testid="search-visa-btn"
              >
                Find Visa Requirements
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filterVOA 
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              onClick={() => setFilterVOA(!filterVOA)}
              data-testid="filter-voa"
            >
              <Filter className="w-4 h-4" />
              Visa on Arrival Only
            </button>
          </div>
        </div>
      </section>

      {/* Countries Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h2 className="text-2xl font-heading text-[#0F172A]">
            All Destinations
            <span className="text-[#64748B] font-body text-base ml-2">
              ({filteredCountries.length} countries)
            </span>
          </h2>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <Input
              type="text"
              placeholder="Filter countries..."
              className="pl-10 h-10 rounded-full border-slate-200"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="filter-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="skeleton h-12 w-12 rounded-lg mb-4"></div>
                <div className="skeleton h-6 w-32 mb-2"></div>
                <div className="skeleton h-4 w-24"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="countries-grid">
            {filteredCountries.map((country) => (
              <button
                key={country.code}
                onClick={() => navigate(`/visa/${country.code}`)}
                className="country-card text-left bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#E15636]/30 group"
                data-testid={`country-card-${country.code}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-4xl">{country.flag}</span>
                  {country.is_visa_on_arrival && (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      Visa on Arrival
                    </Badge>
                  )}
                </div>
                <h3 className="text-xl font-heading text-[#0F172A] mb-2">{country.name}</h3>
                <div className="flex items-center text-[#E15636] font-medium text-sm group-hover:gap-2 transition-all">
                  <span>View Requirements</span>
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && filteredCountries.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B]">No countries found matching your criteria</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default VisaFinderPage;
